
import { Product } from '@socialflows/shared-lib/src/entities/product.entity';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type CartItem = Product & {
  quantity: number;
  selectedVariants: Record<string, string>; // e.g., { "Color": "Black", "Size": "M" }
};

type CartContextType = {
  items: CartItem[];
  addToCart: (
    product: Product,
    quantity: number,
    selectedVariants?: Record<string, string>
  ) => void;
  removeFromCart: (productId: string, selectedVariants?: Record<string, string>) => void;
  updateQuantity: (
    productId: string,
    quantity: number,
    selectedVariants?: Record<string, string>
  ) => void;
  updateVariants: (
    productId: string,
    oldVariants: Record<string, string>,
    newVariants: Record<string, string>
  ) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
};

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart from local storage', e);
      }
    }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('cart', JSON.stringify(items));
    }
  }, [items, isLoaded]);

  const addToCart = (
    product: Product,
    quantity: number,
    selectedVariants: Record<string, string> = {}
  ) => {
    setItems((prev) => {
      const existingItemIndex = prev.findIndex(
        (item) =>
          item.id === product.id &&
          JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants)
      );

      if (existingItemIndex > -1) {
        const newItems = [...prev];
        newItems[existingItemIndex].quantity += quantity;
        return newItems;
      } else {
        return [...prev, { ...product, quantity, selectedVariants }];
      }
    });
  };

  const removeFromCart = (productId: string, selectedVariants: Record<string, string> = {}) => {
    setItems((prev) =>
      prev.filter(
        (item) =>
          !(
            item.id === productId &&
            JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants)
          )
      )
    );
  };

  const updateQuantity = (
    productId: string,
    quantity: number,
    selectedVariants: Record<string, string> = {}
  ) => {
    if (quantity <= 0) {
      removeFromCart(productId, selectedVariants);
      return;
    }

    setItems((prev) =>
      prev.map((item) =>
        item.id === productId &&
        JSON.stringify(item.selectedVariants) === JSON.stringify(selectedVariants)
          ? { ...item, quantity }
          : item
      )
    );
  };

  const updateVariants = (
    productId: string,
    oldVariants: Record<string, string>,
    newVariants: Record<string, string>
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === productId &&
        JSON.stringify(item.selectedVariants) === JSON.stringify(oldVariants)
          ? { ...item, selectedVariants: newVariants }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const cartTotal = items.reduce((total, item) => total + item.price * item.quantity, 0);
  // Count unique items (different variants count as different items)
  const cartCount = items.length;

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateVariants,
        clearCart,
        cartTotal,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
